import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, Loading, Empty, PageHeader, ExportBar, exportExcel, exportCSV, ScoreBadge } from "./_shared";
import { AlertTriangle, Flag } from "lucide-react";

const DIFF_COLOR = { easy: "text-emerald-700 bg-emerald-50", medium: "text-amber-700 bg-amber-50", hard: "text-red-700 bg-red-50" };

export default function ScenarioAnalytics() {
  const { query } = useFilters();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/scenarios${query}`).then((r) => setRows(r.data.scenarios)).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  const scenarios = (rows || []).filter((s) => s.completion_rate > 0 || s.avg_score > 0);
  if (scenarios.length === 0) return <Empty />;

  const flagged = scenarios.filter((s) => s.low_success_flag);

  const handleExcel = () =>
    exportExcel([{
      name: "Scenarios",
      rows: scenarios.map((s) => ({
        Scenario: s.title, Category: s.category, Difficulty: s.difficulty,
        "Completion %": s.completion_rate, "Avg Score": s.avg_score, "Avg Attempts": s.avg_attempts,
        "Avg Time (min)": s.avg_time_min, "Most Missed": s.most_missed.join("; "),
      })),
    }], "scenario-analytics.xlsx");

  return (
    <div data-testid="scenario-analytics-page">
      <PageHeader title="Scenario Analytics"
        description="Effectiveness of every role-play scenario. Scenarios with unusually low success are flagged for instructional review."
        right={<ExportBar onExcel={handleExcel} onCSV={() => exportCSV(scenarios, "scenarios.csv")} />} />

      {flagged.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="flagged-scenarios">
          <AlertTriangle className="w-5 h-5 text-[#b81f24] shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-[#b81f24]">{flagged.length} scenario(s) flagged for review</div>
            <div className="text-xs text-stone-600 mt-0.5">Low average success rate — consider revising the brief or adding scaffolding: {flagged.map((s) => s.title).join(", ")}</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {scenarios.map((s) => (
          <Panel key={s.scenario_id} testid={`scenario-card-${s.scenario_id}`}
            title={s.title}
            subtitle={s.category}
            right={<div className="flex items-center gap-2">
              {s.low_success_flag && <Flag className="w-4 h-4 text-[#b81f24]" />}
              <ScoreBadge value={s.avg_score} />
            </div>}>
            <div className="flex items-center gap-2 mb-4">
              <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium capitalize ${DIFF_COLOR[s.difficulty] || "bg-stone-100 text-stone-600"}`}>{s.difficulty}</span>
              {s.competencies.slice(0, 3).map((c) => (
                <span key={c} className="text-[11px] px-2 py-0.5 rounded-md bg-stone-100 text-stone-600">{c}</span>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center bg-stone-50 rounded-lg py-2">
                <div className="font-semibold tabular-nums text-stone-900">{s.completion_rate}%</div>
                <div className="text-[10px] text-stone-500 uppercase">Completion</div>
              </div>
              <div className="text-center bg-stone-50 rounded-lg py-2">
                <div className="font-semibold tabular-nums text-stone-900">{s.avg_attempts}</div>
                <div className="text-[10px] text-stone-500 uppercase">Avg attempts</div>
              </div>
              <div className="text-center bg-stone-50 rounded-lg py-2">
                <div className="font-semibold tabular-nums text-stone-900">{s.avg_time_min}m</div>
                <div className="text-[10px] text-stone-500 uppercase">Avg time</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[#b45309] mb-1.5">Most Missed</div>
                <ul className="space-y-1">
                  {s.most_missed.length === 0 && <li className="text-xs text-stone-400">—</li>}
                  {s.most_missed.map((m) => <li key={m} className="text-xs text-stone-600">• {m}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1.5">Highest Rated</div>
                <ul className="space-y-1">
                  {s.highest_rated.length === 0 && <li className="text-xs text-stone-400">—</li>}
                  {s.highest_rated.map((m) => <li key={m} className="text-xs text-stone-600">• {m}</li>)}
                </ul>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
